import useResizeObserver from '@react-hook/resize-observer';
import { axisBottom, scaleBand, scaleLinear, select } from 'd3';
import dayjs from 'dayjs';
import { FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';

import { Loader, useDialog } from 'components';
import { SelectedLocationContext } from 'contexts/selected-location';
import fetchWithRetry from 'services/fetch-retry';
import { ChartComponentProps } from '../chart-card';
import DateRangeDialog from './date-range';

const MARGIN_X = 24;
const MARGIN_Y = 32;
const FONT_SIZE = 16;
const DATETIME_LOCAL = 'YYYY-MM-DDTHH:mm';

type InventoryCount = {
  date: string;
  totalCount: number;
  loadedCount: number;
  gensetCount: number;
  chassisCount: number;
};

const Inventory: FC<ChartComponentProps> = ({ boxRef, setTitleElement }) => {
  const navigate = useNavigate();
  const { selectedLocation } = useContext(SelectedLocationContext);
  const settingsDialog = useDialog();

  /* refs */
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  /* state */
  const [{ width, height }, setDimensions] = useState({ height: 0, width: 0 });
  const [countsUnfiltered, setCounts] = useState<InventoryCount[]>([]);
  const [filter, setFilter] = useState<InventoryCount>({
    date: '',
    totalCount: 1,
    loadedCount: 1,
    gensetCount: 0,
    chassisCount: 0
  });
  const [hovered, setHovered] = useState<[MouseEvent, InventoryCount]>();
  const [timeRange, setTimeRange] = useState<number>(14);
  const [loading, setLoading] = useState<boolean>(false);

  const countsMap: Map<string, InventoryCount> = useMemo(
    () => countsUnfiltered.reduce((acc, c) => acc.set(c.date, c), new Map()),
    [countsUnfiltered]
  );

  const filteredCounts: InventoryCount[] = useMemo(
    () =>
      countsUnfiltered.map(c => ({
        date: c.date,
        totalCount: c.totalCount * filter.totalCount,
        loadedCount: c.loadedCount * filter.loadedCount,
        gensetCount: c.gensetCount * filter.gensetCount,
        chassisCount: c.chassisCount * filter.chassisCount
      })),
    [countsUnfiltered, filter]
  );

  /* layout effects */
  useLayoutEffect(() => {
    setDimensions(() => ({
      height: (boxRef.current?.clientHeight ?? MARGIN_Y) - MARGIN_Y,
      width: (boxRef.current?.clientWidth ?? MARGIN_X) - MARGIN_X
    }));
  }, [boxRef]);

  useResizeObserver(boxRef.current, ({ contentBoxSize }) => {
    setDimensions({
      height: contentBoxSize[0].blockSize - MARGIN_Y,
      width: contentBoxSize[0].inlineSize - MARGIN_X
    });
  });

  useEffect(
    () =>
      setTitleElement?.(
        <div className="flex-grow-1 d-flex justify-content-end">
          <button className="btn-back p-2" onClick={settingsDialog.openDialog}>
            <span className="material-icons icon-large">settings</span>
          </button>
        </div>
      ),
    [settingsDialog.openDialog, setTitleElement]
  );

  /* data hooks */

  const startDate = useMemo(
    () =>
      dayjs()
        .startOf('day')
        .subtract(timeRange - 1, 'day'),
    [timeRange]
  );

  const getCounts = useCallback(async () => {
    setLoading(true);
    const url = new URL(`/api/units/counts/date`, window.location.origin);
    if (selectedLocation) {
      url.searchParams.append('location', String(selectedLocation.id));
    }
    url.searchParams.append('msDateFrom', startDate.valueOf().toString());
    setCounts(await fetchWithRetry<InventoryCount[]>(url, { method: 'GET' }, []));
    setLoading(false);
  }, [selectedLocation, startDate]);

  const timeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    setLoading(true);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(getCounts, 500);
  }, [getCounts]);

  const dateKeys = useMemo(() => filteredCounts.map(({ date }) => dayjs.utc(date).format('M/D')), [filteredCounts]);

  const noData: boolean = useMemo(() => !filteredCounts?.some(data => data.totalCount), [filteredCounts]);

  /* callbacks */

  const handleClick = useCallback(
    (_: MouseEvent, { date }: InventoryCount) => {
      navigate({
        pathname: '/units',
        search: createSearchParams({
          start: encodeURIComponent(dayjs(date).format(DATETIME_LOCAL)),
          end: encodeURIComponent(dayjs(date).add(1, 'day').format(DATETIME_LOCAL))
        }).toString()
      });
    },
    [navigate]
  );

  const renderBarPlot = useCallback(() => {
    const svg = select(svgRef.current);
    svg.selectAll('g').remove();

    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;');

    // Declare the x (horizontal position) scale.
    const x = scaleBand().domain(dateKeys).range([MARGIN_X, width]).padding(0.1);

    // Declare the y (vertical position) scale.
    const y = scaleLinear()
      .domain([0, Math.max(...filteredCounts.map(d => d.totalCount))])
      .range([height - MARGIN_Y, MARGIN_Y]);

    svg
      .append('g')
      .selectAll()
      .data(filteredCounts)
      .join('text')
      .attr('x', d => `${x(dayjs.utc(d.date).format('M/D')) ?? 0}`)
      .attr('y', d => y(d.totalCount) - 4)
      .text(d => d.totalCount);

    // Add a rect for each bar.
    svg
      .append('g')
      .attr('class', 'fill-gate-out')
      .selectAll()
      .data(filteredCounts)
      .join('rect')
      .attr('x', d => `${x(dayjs.utc(d.date).format('M/D')) ?? 0}`)
      .attr('y', d => y(d.totalCount))
      .attr('height', d => y(0) - y(d.loadedCount))
      .attr('width', x.bandwidth())
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    svg
      .append('g')
      .attr('class', 'fill-gate-in')
      .selectAll()
      .data(filteredCounts)
      .join('rect')
      .attr('x', d => `${x(dayjs.utc(d.date).format('M/D')) ?? 0}`)
      .attr('y', d => y(d.totalCount - d.loadedCount))
      .attr('height', d => y(0) - y(d.totalCount - d.loadedCount - d.gensetCount - d.chassisCount))
      .attr('width', x.bandwidth())
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    svg
      .append('g')
      .attr('class', 'fill-lift-on')
      .selectAll()
      .data(filteredCounts)
      .join('rect')
      .attr('x', d => `${x(dayjs.utc(d.date).format('M/D'))}`)
      .attr('y', d => y(d.gensetCount + d.chassisCount))
      .attr('height', d => y(0) - y(d.gensetCount))
      .attr('width', x.bandwidth())
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    svg
      .append('g')
      .attr('class', 'fill-lift-off')
      .selectAll()
      .data(filteredCounts)
      .join('rect')
      .attr('x', d => `${x(dayjs.utc(d.date).format('M/D'))}`)
      .attr('y', d => y(d.chassisCount))
      .attr('height', d => y(0) - y(d.chassisCount))
      .attr('width', x.bandwidth())
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    // Add the x-axis and label.
    svg
      .append('g')
      .attr('transform', `translate(0,${height - MARGIN_Y})`)
      .call(axisBottom(x).tickSizeOuter(0))
      .call(g => g.selectAll('.tick text').attr('font-size', FONT_SIZE));

    // Add the y-axis and label, and remove the domain line.
    // svg
    //   .append('g')
    //   .attr('transform', `translate(${MARGIN_X},0)`)
    //   .call(
    //     axisLeft(y)
    //       .ticks(Math.min(Math.floor(height / 50), Math.max(...filteredCounts.map(d => d.totalCount))))
    //       .tickFormat(format('.0f'))
    //   )
    //   .call(g => g.selectAll('.tick text').attr('font-size', FONT_SIZE))
    //   .call(g => g.select('.domain').remove());
  }, [filteredCounts, dateKeys, height, width, handleClick]);

  const renderTooltip = useCallback(() => {
    const tooltip = select(tooltipRef.current);
    const size = tooltip.node()?.getBoundingClientRect();
    if (hovered?.[0]?.target instanceof SVGRectElement) {
      const x = Math.max(0, Math.min(width - (size?.width ?? 0), hovered[0].offsetX));
      const y = Math.max(0 + (size?.height ?? 0), Math.min(height, hovered[0].offsetY));
      const data = countsMap.get(hovered[1].date);
      if (!data) return;
      tooltip
        .style('transform', `translate(${x}px,calc(${y}px - 100%))`)
        .style('opacity', 1)
        .html(
          `${dayjs.utc(data.date).format('MMMM D')}<br/>\
          Loaded: ${data.loadedCount}<br/>\
          Empty: ${data.totalCount - data.loadedCount - data.gensetCount - data.chassisCount}<br/>\
          Genset: ${data.gensetCount}<br/>\
          Chassis: ${data.chassisCount}<br/>`
        );
    } else {
      tooltip.style('opacity', 0);
    }
  }, [countsMap, hovered, height, width]);

  useEffect(renderBarPlot, [renderBarPlot]);
  useEffect(renderTooltip, [renderTooltip]);

  return (
    <div id="transaction-bar-chart" className="h-100">
      <Loader loading={loading}>
        <div className="tooltip p-3" ref={tooltipRef} />
        <div className="bar-chart-legend d-flex justify-content-between">
          <div className="d-flex g-2">
            <div className="bg-gate-out" />
            <label>LOADED</label>
          </div>
          <div className="d-flex g-2">
            <div className="bg-gate-in" />
            <label>EMPTY</label>
          </div>
          <div className="d-flex g-2">
            <button
              className="btn-outline bg-lift-on p-0"
              disabled={!filter.chassisCount && !filter.loadedCount}
              onClick={() => setFilter(p => ({ ...p, gensetCount: Number(!p.gensetCount) }))}
              value={filter.gensetCount}
            />
            <label>GENSET</label>
          </div>
          <div className="d-flex g-2">
            <button
              className="btn-outline bg-lift-off p-0"
              disabled={!filter.gensetCount && !filter.loadedCount}
              onClick={() => setFilter(p => ({ ...p, chassisCount: Number(!p.chassisCount) }))}
              value={filter.chassisCount}
            />
            <label>CHASSIS</label>
          </div>
        </div>
        <svg ref={svgRef} />
        {noData && <p className="no-transactions">NO DATA</p>}
      </Loader>
      <DateRangeDialog dialogProps={settingsDialog} groupBy="day" timeRange={timeRange} setTimeRange={setTimeRange} />
    </div>
  );
};

export default Inventory;
